// Third-party packages
import axios from 'axios'
import Cookies from 'js-cookie'

export const getSessionId = () => {
  const sessionId = Cookies.get('session_id')
  return sessionId ? decodeURIComponent(sessionId) : null
}

// Enhanced HTTP tracking function
const trackHttpRequest = (method, url, startTime, response, error = null) => {
  const duration = Date.now() - startTime;
  const requestSize = method === 'POST' || method === 'PUT' ? JSON.stringify(response?.config?.data || {}).length : 0;
  const responseSize = response?.data ? JSON.stringify(response.data).length : 0;
  const status = response?.status || (error?.response?.status) || 0;

  if (window.faroTracker) {
    window.faroTracker.trackApiCall(method, url, status, duration, requestSize, responseSize, error);
    
    // Track business-specific metrics
    if (url.includes('/product/search')) {
      window.faroTracker.trackEvent('product_search', {
        duration,
        results_count: response?.data?.products?.length || 0,
        total_results: response?.data?.total_results || 0,
        success: !error
      });
    } else if (url.includes('/cart/')) {
      const operation = method === 'PUT' ? 'add' : method === 'DELETE' ? 'remove' : 'view';
      const productId = url.split('/cart/')[1]?.split('/')[0];
      window.faroTracker.trackEvent('cart_api_call', {
        operation,
        product_id: productId,
        duration,
        success: !error
      });
    } else if (url.includes('/checkout/process')) {
      window.faroTracker.trackEvent('checkout_api_call', {
        duration,
        success: !error,
        error_type: error ? window.faroTracker.categorizePaymentError(error) : null
      });
    }
  }
}

// Create axios interceptors for automatic tracking
axios.interceptors.request.use(
  (config) => {
    config.metadata = { startTime: Date.now() };
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => {
    if (response.config.metadata) {
      trackHttpRequest(
        response.config.method?.toUpperCase(),
        response.config.url,
        response.config.metadata.startTime,
        response
      );
    }
    return response;
  },
  (error) => {
    if (error.config?.metadata) {
      trackHttpRequest(
        error.config.method?.toUpperCase(),
        error.config.url,
        error.config.metadata.startTime,
        error.response,
        error
      );
    }
    return Promise.reject(error);
  }
);

export const healthz = async () => {
  return await axios.request({
    method: 'get',
    url: '/healthz',
    timeout: 4000,
    validateStatus: (status) => true,
  })
}

////  Main Routes  /////////////////////////////////////////////////////////////

export const cartGet = async () => {
  return await axios.request({
    method: 'post',
    url: '/api/v1/cart',
    data: {
      session_id: getSessionId()
    },
    timeout: 10000, // Standard timeout for cart operations
    validateStatus: (status) => true,
  })
}

export const cartItemGet = async (productId) => {
  return await axios.request({
    method: 'get',
    url: `/api/v1/cart/${productId}`,
    data: {
      session_id: getSessionId()
    },
    timeout: 10000, // Standard timeout for cart operations
    validateStatus: (status) => true,
  })
}

export const cartItemAdd = async (productId, quantity) => {
  return await axios.request({
    method: 'put',
    url: `/api/v1/cart/${productId}/${quantity}`,
    data: {
      session_id: getSessionId()
    },
    timeout: 10000, // Standard timeout for cart operations
    validateStatus: (status) => true,
  })
}

export const cartItemRemove = async (productId) => {
  return await axios.request({
    method: 'delete',
    url: `/api/v1/cart/${productId}`,
    data: {
      session_id: getSessionId()
    },
    timeout: 10000, // Standard timeout for cart operations
    validateStatus: (status) => true,
  })
}

export const checkout = async (data) => {
  data.session_id = getSessionId()
  return await axios.request({
    method: 'post',
    url: '/api/v1/checkout/process',
    data: data,
    timeout: 15000, // Increased to 15 seconds to handle Lambda processing time
    validateStatus: (status) => true,
  })
}

export const productDocuments = async (ids) => {
  return await axios.request({
    method: 'post',
    url: '/api/v1/product/documents',
    data: ids,
    timeout: 10000, // Standard timeout for database queries
    validateStatus: (status) => true,
  })
}

export const productSearch = async (data) => {
  return await axios.request({
    method: 'post',
    url: '/api/v1/product/search',
    data: data,
    timeout: 10000, // Standard timeout for database queries
    validateStatus: (status) => true,
  })
}

export const productSuggestions = async (data) => {
  return await axios.request({
    method: 'post',
    url: '/api/v1/product/suggestions',
    data: data,
    timeout: 10000, // Standard timeout for database queries
    validateStatus: (status) => true,
  })
}

////  Customer Authentication Routes  ////////////////////////////////////////

export const customerRegister = async (data) => {
  return await axios.request({
    method: 'post',
    url: '/api/v1/customer/register',
    data: data,
    timeout: 8000,
    validateStatus: (status) => true,
  })
}

export const customerLogin = async (data) => {
  return await axios.request({
    method: 'post',
    url: '/api/v1/customer/login',
    data: data,
    timeout: 8000,
    validateStatus: (status) => true,
  })
}

export const customerProfile = async () => {
  const token = localStorage.getItem('auth_token')
  return await axios.request({
    method: 'get',
    url: '/api/v1/customer/profile',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    timeout: 4000,
    validateStatus: (status) => true,
  })
}

export const customerUpdateProfile = async (data) => {
  const token = localStorage.getItem('auth_token')
  return await axios.request({
    method: 'put',
    url: '/api/v1/customer/profile',
    data: data,
    headers: {
      'Authorization': `Bearer ${token}`
    },
    timeout: 4000,
    validateStatus: (status) => true,
  })
}

export const customerAddAddress = async (data) => {
  const token = localStorage.getItem('auth_token')
  return await axios.request({
    method: 'post',
    url: '/api/v1/customer/addresses',
    data: data,
    headers: {
      'Authorization': `Bearer ${token}`
    },
    timeout: 4000,
    validateStatus: (status) => true,
  })
}

export const customerOrders = async () => {
  const token = localStorage.getItem('auth_token')
  const customerId = localStorage.getItem('customer_id')
  return await axios.request({
    method: 'get',
    url: `/api/v1/order-management/customer/${customerId}/orders`,
    headers: {
      'Authorization': `Bearer ${token}`
    },
    timeout: 10000,
    validateStatus: (status) => true,
  })
}

export const customerVerifyToken = async (token) => {
  return await axios.request({
    method: 'post',
    url: '/api/v1/customer/verify-token',
    data: { token },
    timeout: 4000,
    validateStatus: (status) => true,
  })
}

export const trackOrder = async (orderId) => {
  return await axios.request({
    method: 'get',
    url: `/api/v1/tracking/${orderId}`,
    timeout: 4000,
    validateStatus: (status) => true,
  })
}

export const getRecommendations = async (params = {}) => {
  const queryParams = new URLSearchParams()
  
  if (params.customer_id) queryParams.append('customer_id', params.customer_id)
  if (params.product_id) queryParams.append('product_id', params.product_id)
  if (params.cart_items) queryParams.append('cart_items', params.cart_items)
  if (params.limit) queryParams.append('limit', params.limit)
  
  const queryString = queryParams.toString()
  const url = `/api/v1/recommendations${queryString ? `?${queryString}` : ''}`
  
  return await axios.request({
    method: 'get',
    url: url,
    timeout: 10000, // Azure Function might take a moment
    validateStatus: (status) => true,
  })
}

// Helper functions for authentication
export const isAuthenticated = () => {
  const token = localStorage.getItem('auth_token')
  const customerId = localStorage.getItem('customer_id')
  return !!(token && customerId)
}

export const getCustomerInfo = () => {
  if (!isAuthenticated()) return null
  
  return {
    id: localStorage.getItem('customer_id'),
    email: localStorage.getItem('customer_email'),
    name: localStorage.getItem('customer_name'),
    token: localStorage.getItem('auth_token')
  }
}

export const logout = () => {
  // Clear authentication data
  localStorage.removeItem('auth_token')
  localStorage.removeItem('customer_id')
  localStorage.removeItem('customer_email')
  localStorage.removeItem('customer_name')
  
  // Track logout event
  if (window.faroTracker) {
    window.faroTracker.trackEvent('customer_logout', {
      logout_method: 'manual'
    })
  }
}

export default {
  healthz,
  cartGet,
  cartItemGet,
  cartItemAdd,
  cartItemRemove,
  checkout,
  productDocuments,
  productSearch,
  productSuggestions,
  customerRegister,
  customerLogin,
  customerProfile,
  customerUpdateProfile,
  customerAddAddress,
  customerOrders,
  customerVerifyToken,
  trackOrder,
  getRecommendations,
  getSessionId,
  isAuthenticated,
  getCustomerInfo,
  logout
}
